import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExpertProfileCreateDto, ExpertProfilePatchDto, ExpertProfileSelectDto } from '../types/api.types';
import { removeEmptyStrings } from '../utils/form-validator.util';

export const useCreateExpertProfile = () => {
  const { apiPost } = useApi({ showToastOnError: true });
  const queryClient = useQueryClient();

  const onError = (error: ApiErrorWrapper) => {
    console.error('Error submitting expert profile:', error);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.expertProfile.getExpertProfile,
    });
  };

  return useMutation<ExpertProfileSelectDto, ApiErrorWrapper, ExpertProfileCreateDto>({
    mutationFn: async (formData) => {
      const cleanedFormData = removeEmptyStrings(formData, true);
      return await apiPost<ExpertProfileSelectDto>({
        path: '/v1/expert-profiles',
        body: cleanedFormData,
      });
    },
    onError,
    onSuccess,
  });
};

export const useUpdateExpertProfile = () => {
  const { apiPatch } = useApi({ showToastOnError: true });
  const queryClient = useQueryClient();

  const onError = (error: ApiErrorWrapper) => {
    console.error('Error updating expert profile:', error);
  };

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.expertProfile.getExpertProfile,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.getCurrentUser,
    });
  };

  return useMutation<ExpertProfileSelectDto, ApiErrorWrapper, ExpertProfilePatchDto>({
    mutationFn: async (formData) => {
      const cleanedFormData = removeEmptyStrings(formData, true);
      return await apiPatch<ExpertProfileSelectDto>({
        path: '/v1/expert-profiles',
        body: cleanedFormData,
      });
    },
    onError,
    onSuccess,
  });
};

export const useCurrentUserExpertProfile = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(isAuthenticated);

  return useQuery<ExpertProfileSelectDto | null, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const response = await apiGet<ExpertProfileSelectDto>({ path: '/v1/expert-profiles' });
      return response ?? null;
    },
    queryKey: queryKeys.expertProfile.getExpertProfile,
  });
};

export const useExpertProfileForUser = (userId: string) => {
  const { apiGet } = useApi();

  return useQuery<ExpertProfileSelectDto | null, ApiErrorWrapper>({
    queryFn: async () => {
      const response = await apiGet<ExpertProfileSelectDto>({ path: `/v1/experts/${userId}/expert-profile` });
      return response ?? null;
    },
    queryKey: queryKeys.expertProfile.getExpertProfileForUser(userId),
  });
};
